/**
 * AdminScreen.js
 *
 * Grand Auction Admin View (usually on a projector)
 * Left: Poster (Bollywood library) or a big card with the item's name
 * Right: Item details, its score, its other attributes, and the Sell / Skip controls
 * The host can end the auction early; selling the last item ends it too.
 */

import React, { useState } from "react";
import toast from "react-hot-toast";
import Button from "../../../components/ui/Button";
import SellItemModal from "../Modals/SellItem";
import { finishAuction, showItem, skipItem } from "../helpers/auctionLobby";
import { getRoomUpdatesAfterItem, getRoundOrder } from "../helpers/saleRules";
import { sellItemToTeam } from "../helpers/sellItemToTeam";
import { getAuctionSettings } from "../assets/Rules";
import { getItemPoster, getRoomLibrary } from "../library/libraries";

const AdminScreen = ({ room, teams }) => {
    const [showModal, setShowModal] = useState(false);

    const library = getRoomLibrary(room);
    const items = library.items;

    // The current item lives on the room, so every screen shows the same one.
    // Rooms from before this existed fall back to the number of items sold.
    const currentItemIndex = Math.min(room.currentMovieIndex ?? (room.moviesSold || 0), items.length - 1);
    const currentItem = items[currentItemIndex];
    const poster = getItemPoster(library, currentItem);
    const roundOrder = getRoundOrder(room, items.length);
    const positionInRound = roundOrder.indexOf(currentItemIndex) + 1;
    const isComebackRound = room.round === 2;
    const isLastItem = positionInRound === roundOrder.length;
    const itemSold = room.lastSale?.itemIndex !== undefined
        ? room.lastSale.itemIndex === currentItemIndex
        : room.lastSale?.movieName === currentItem.name;
    const settings = getAuctionSettings(room);

    const otherAttributes = library.attributes.filter((attribute) => attribute.kind !== "name" && attribute.kind !== "score");

    /**
     * Sells the current item, and in the same write moves everyone on:
     * to the next item, to a break if the host asked for one after this many sales,
     * or to the results after the last item.
     */
    const handleSell = (team, amount) => {
        const roomUpdates = getRoomUpdatesAfterItem(room, settings, currentItemIndex, items.length, true);
        return sellItemToTeam(room.id, team, currentItem, currentItemIndex, amount, roomUpdates);
    };

    /**
     * Nobody bought the current item: move on (it may come back in the comeback round)
     */
    const handleSkip = async () => {
        const roomUpdates = getRoomUpdatesAfterItem(room, settings, currentItemIndex, items.length, false);
        if (!(await skipItem(room.id, roomUpdates))) toast.error("Couldn't skip");
    };

    const handleEndAuction = async () => {
        const next = settings.itemsToSubmit < settings.maxItemsPerTeam ? "Teams will pick what to submit, then see the results." : "Everyone will see the final results.";
        if (!window.confirm(`End the auction now? ${next}`)) return;
        if (!(await finishAuction(room.id, settings))) toast.error("Couldn't end the auction");
    };

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-black via-zinc-900 to-black text-white flex flex-col md:flex-row items-center justify-center overflow-hidden p-4 md:p-10 pt-28 gap-10 md:gap-16">

            {/* Left Half - Poster or item card */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-72 sm:w-96 md:w-[28rem] h-[36rem] rounded-3xl overflow-hidden shadow-[0_0_60px_rgba(255,255,255,0.15)] border border-white/10">
                    {poster ? (
                        <img
                            src={poster}
                            alt={currentItem.name}
                            className="object-cover w-full h-full"
                        />
                    ) : (
                        <div className="flex flex-col justify-between h-full p-8 bg-gradient-to-br from-yellow via-peach to-coral text-black">
                            <span className="text-xl font-bold uppercase tracking-widest">{library.name}</span>
                            <span className="text-5xl md:text-6xl font-extrabold leading-tight break-words">{currentItem.name}</span>
                            <span className="text-2xl font-semibold">#{currentItemIndex + 1} of {items.length}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Half - Details */}
            <div className="flex-1 flex flex-col justify-center items-start text-left px-6 md:px-10">
                <div className="text-2xl text-zinc-400 mb-2">
                    {isComebackRound
                        ? <><span className="text-peach font-semibold">Comeback round</span> · {positionInRound} of {roundOrder.length}</>
                        : <>#{currentItemIndex + 1} of {items.length}</>}
                </div>
                <h1 className="text-5xl md:text-6xl font-bold tracking-wide mb-8 drop-shadow-md">
                    {currentItem.name}
                </h1>

                {/* Score Circle */}
                <div className="flex flex-col items-center justify-center w-40 h-40 rounded-full bg-gradient-to-br from-yellow to-coral shadow-lg text-black mb-10">
                    <span className="text-5xl font-extrabold">{currentItem.score}</span>
                    <span className="text-lg font-semibold">{library.scoreAttribute}</span>
                </div>

                {/* Other attributes */}
                <div className="flex flex-col gap-4 w-full max-w-md">
                    {otherAttributes.map((attribute) => (
                        <div key={attribute.name} className="bg-white/10 border border-white/20 rounded-2xl p-4 flex items-center justify-between gap-4">
                            <span className="text-zinc-400 text-xl font-medium">{attribute.name}</span>
                            <span className="text-2xl font-semibold text-right">{currentItem.values?.[attribute.name] || "—"}</span>
                        </div>
                    ))}
                </div>

                {/* Buttons */}
                <div className="mt-12 flex justify-center w-full max-w-md">
                    {!itemSold ? (
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <Button
                                className="text-3xl py-5 px-14 h-auto bg-yellow hover:bg-white text-black font-extrabold rounded-2xl shadow-lg transition-all"
                                onClick={() => setShowModal(true)}
                            >
                                Sell
                            </Button>
                            <Button
                                className="text-xl py-5 px-8 h-auto bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-2xl transition-all"
                                onClick={handleSkip}
                            >
                                Skip (unsold)
                            </Button>
                        </div>
                    ) : isLastItem ? (
                        <div className="text-3xl font-bold text-yellow">That's everything!</div>
                    ) : (
                        <Button
                            className="text-2xl py-4 px-10 h-auto bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl shadow-lg transition-all"
                            onClick={() => showItem(room.id, currentItemIndex + 1)}
                        >
                            Next →
                        </Button>
                    )}
                </div>
            </div>

            {/* End early */}
            <button
                onClick={handleEndAuction}
                className="fixed bottom-6 right-6 z-40 rounded-2xl border border-zinc-700 bg-zinc-950/90 px-5 py-3 text-zinc-300 backdrop-blur hover:border-zinc-500 hover:text-white transition-colors"
            >
                End auction
            </button>

            {/* Sell Modal */}
            {showModal && (
                <SellItemModal
                    item={currentItem}
                    library={library}
                    teams={teams}
                    settings={settings}
                    onSell={handleSell}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default AdminScreen;
